/* Copyright (c) 2020-present CloudIO Inc */

import { useStateXValue } from '@cloudio-saas/statex';
import { makeStyles } from '@mui/styles';
import { getManager, Page } from 'componentregistry';
import { designPath, embeddedPagesAtom } from 'designer/atoms';
import { usePlatform } from 'platform/PlatformHooks';
import React, { memo, useMemo, useLayoutEffect } from 'react';

const useStyles = makeStyles({
  designer: {
    userSelect: 'none',
  },
});

function PageContainer({
  width,
  height,
  pageId,
  appUid,
  roleUid,
  embedded = false,
  paramString,
}: {
  width: number;
  height: number;
  pageId: string;
  appUid: string;
  roleUid: string;
  embedded?: boolean;
  paramString: string;
}): React.ReactElement {
  const classes = useStyles();
  const platform = usePlatform();
  const devMode = !embedded && platform.isDeveloperMode(pageId);
  const page = useStateXValue<Page | undefined>(
    designPath({ appUid, pageId }),
    undefined,
  );
  const domProps = useMemo(
    () => ({ 'data-testid': `root-${pageId}` }),
    [pageId],
  );
  // console.log('PageContainer.render', { pageId, embedded });
  useLayoutEffect(() => {
    if (embedded) {
      // console.log('PageContainer.adding embeddedPagesAtom', pageId);
      platform.setter(embeddedPagesAtom, (pages = []) =>
        pages.includes(pageId) ? pages : [...pages, pageId],
      );
      return () => {
        // console.log('PageContainer.removing embeddedPagesAtom', pageId);
        platform.setter(embeddedPagesAtom, (pages = []) => {
          // console.log('PageContainer', pages, pageId);
          const newPages = pages.includes(pageId)
            ? pages.filter((p) => p !== pageId)
            : pages;
          // console.log('PageContainer.newPages', newPages);
          return newPages;
        });
      };
    }
    platform.setter(embeddedPagesAtom, (pages = []) =>
      pages.includes(pageId) ? pages.filter((p) => p !== pageId) : pages,
    );
  }, [embedded, pageId, platform]);

  if (!page) {
    return (
      <span>
        Missing page with id {appUid}.{pageId}
      </span>
    );
  }
  const item = page.root;
  if (!item) {
    return <span>Missing item with itemId root</span>;
  }
  const mgr = getManager(item.type);

  if (!mgr || mgr.managerType !== 'Component') {
    return (
      <span>Unknown item type {item.type} used in item with itemId root</span>
    );
  }

  const { Component, DEVComponent } = mgr;

  if (!devMode) {
    if (!Component) {
      return <div>Missing Component!</div>;
    }
    return (
      <Component
        key={`${pageId}-${appUid}`}
        appUid={appUid}
        roleUid={roleUid}
        pageId={pageId}
        paramString={paramString}
        itemId="root"
        itemType={mgr.type}
        platform={platform}
        width={width}
        height={height}
        domProps={domProps}
      />
    );
  }
  if (!DEVComponent) {
    return <div>Missing DEVComponent!</div>;
  }
  return (
    <DEVComponent
      key={`${pageId}-${appUid}`}
      appUid={appUid}
      roleUid={roleUid}
      pageId={pageId}
      paramString={paramString}
      itemId="root"
      itemType={mgr.type}
      platform={platform}
      width={width}
      height={height}
      domProps={domProps}
      className={classes.designer}
    />
  );
}

export default memo(PageContainer);
